import React from 'react';
import { Play, Square, Clock, Activity, Zap, Loader2, RefreshCw } from 'lucide-react';
import Button from '../../../components/ui/Button';


const ScanControlPanel = ({ 
  isScanning, 
  onToggleScan, 
  sessionTimer, 
  apiStatus,
  lastScanTime,
  onManualRefresh 
}) => {
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours?.toString()?.padStart(2, '0')}:${minutes?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const getStatusColor = () => {
    switch (apiStatus) {
      case 'connected':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      case 'connecting':
        return 'text-yellow-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusText = () => {
    switch (apiStatus) {
      case 'connected':
        return 'Reader RFID Conectado';
      case 'error':
        return 'Error de Reader RFID';
      case 'connecting':
        return 'Conectando...';
      default:
        return 'Desconectado';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            Panel de Control de Escaneo
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Monitoreo automático del Reader RFID
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Manual Refresh Button */}
          {!isScanning && onManualRefresh && (
            <Button
              onClick={onManualRefresh}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </Button>
          )}
          
          {/* Main Scan Toggle Button */}
          <Button
            onClick={onToggleScan}
            variant={isScanning ? "destructive" : "default"}
            size="lg"
            className="flex items-center gap-2 min-w-[140px]"
          >
            {isScanning ? (
              <>
                <Square className="w-5 h-5" />
                Detener
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Iniciar Escaneo
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Status Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Scan Status */}
        <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
          <div className={`w-3 h-3 rounded-full ${isScanning ? 'bg-green-500' : 'bg-gray-400'}`} />
          <div>
            <p className="text-sm font-medium text-foreground">
              {isScanning ? 'Escaneando' : 'Detenido'}
            </p>
            <p className="text-xs text-muted-foreground">
              Estado del sistema
            </p>
          </div>
        </div>

        {/* API Status */}
        <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
          <Activity className={`w-4 h-4 ${getStatusColor()}`} />
          <div>
            <p className={`text-sm font-medium ${getStatusColor()}`}>
              {getStatusText()}
            </p>
            <p className="text-xs text-muted-foreground">
              Estado del Reader
            </p>
          </div>
        </div>

        {/* Session Timer */}
        <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
          <Clock className="w-4 h-4 text-blue-600" />
          <div>
            <p className="text-sm font-medium text-foreground">
              {formatTime(sessionTimer)}
            </p>
            <p className="text-xs text-muted-foreground">
              Tiempo de sesión
            </p>
          </div>
        </div>

        {/* Last Scan Time */}
        <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
          <Zap className="w-4 h-4 text-purple-600" />
          <div>
            <p className="text-sm font-medium text-foreground">
              {lastScanTime ? formatTime(Math.floor((new Date() - new Date(lastScanTime)) / 1000)) + ' atrás' : 'Nunca'}
            </p>
            <p className="text-xs text-muted-foreground">
              Último escaneo
            </p>
          </div>
        </div>
      </div>

      {/* Scanning Info */}
      {isScanning && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="animate-spin">
              <Loader2 className="w-4 h-4 text-blue-600" />
            </div>
            <p className="text-sm text-blue-800">
              Monitoreando el Reader RFID para detectar nuevos vehículos...
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScanControlPanel;